import React from 'react';
import TrafficLandingTemplate, { LandingPageConfig } from './TrafficLandingTemplate';
import { Activity, BarChart3, Globe } from 'lucide-react';

const config: LandingPageConfig = {
    slug: 'he/buy-targeted-traffic',
    titleTag: 'Buy Targeted Traffic | Country & Niche Targeting | Traffic Creator',
    metaDescription: 'Buy targeted traffic from specific countries, devices, and niches. Real residential visitors precisely matched to your audience. Verified in GA4.',
    keywords: 'Buy Targeted Traffic, Buy Targeted Website Traffic, Buy Targeted Web Traffic, Targeted Traffic That Converts, Buy Keyword Targeted Traffic',
    primaryKeyword: 'Buy Targeted Traffic',
    secondaryKeyword: 'Targeted Traffic That Converts',
    reviewCount: '518',
    ratingValue: '4.9',

    h1: 'Buy Targeted Traffic That Converts into Real Results',
    subheadline: 'Generic traffic is wasted budget. Our platform lets you buy targeted traffic from exact countries, devices, and referrer paths — matching your ideal audience profile perfectly.',

    problemH2: 'Why Untargeted Traffic Wastes Your Budget',
    problemText: 'Sending random visitors from irrelevant countries and devices to your website creates terrible bounce rates and zero conversions. You need traffic that matches your actual audience — the right geography, the right device, and the right browsing behavior.',
    solutionPoints: [
        'Precision geo-targeting from 190+ countries down to city level',
        'Device targeting — desktop, mobile, or tablet traffic on demand',
        'Custom referrer paths to simulate traffic from Google, social media, or direct',
        'Keyword-targeted sessions that arrive via search-like pathways'
    ],

    featuresH2: 'How Our Targeted Traffic Platform Works',
    features: [
        { icon: <Globe />, title: 'Country & City Targeting', description: 'Choose traffic from any country worldwide. Need visitors specifically from New York, London, or Tokyo? Our residential network delivers with pinpoint accuracy.' },
        { icon: <BarChart3 />, title: 'Behavioral Targeting', description: 'Configure exactly how visitors interact — dwell time, pages per session, scroll depth, and bounce rate — to match your niche\'s natural engagement patterns.' },
        { icon: <Activity />, title: 'Referrer Source Control', description: 'Make traffic appear as if it came from Google search, social media, or direct visits. Full control over the referrer path for realistic Analytics data.' }
    ],

    testimonialsH2: 'What Our Targeted Traffic Clients Say',
    testimonials: [
        { name: 'David L.', role: 'E-commerce Director', result: 'USA Traffic +400%', text: 'We needed US-only traffic for our Shopify store. The geo-targeting accuracy was incredible — 98% of visits came from our target states.', seed: 'davidl' },
        { name: 'Nina S.', role: 'Media Buyer', result: 'Perfect Device Mix', text: 'Being able to buy targeted traffic split 70/30 mobile/desktop matched our actual audience perfectly. The Analytics looked completely natural.', seed: 'ninas' },
        { name: 'Carlos M.', role: 'Local Business Owner', result: 'City-Level Precision', text: 'I bought keyword targeted traffic for my local service area. All visits came from my city and neighboring areas. Incredible precision.', seed: 'carlosm' }
    ],

    ctaH2: 'Ready to Buy Targeted Traffic That Matches Your Audience?',

    faqs: [
        { question: 'What does buy targeted traffic mean exactly?', answer: 'Buying targeted traffic means purchasing website visitors that match specific criteria — geographic location, device type, referrer source, and behavioral patterns. Unlike generic traffic, targeted traffic aligns with your actual audience profile.' },
        { question: 'Can I buy targeted traffic from a specific country?', answer: 'Yes! We offer geo-targeting from 190+ countries. You can target by country, region, or even city level. Our residential IP network ensures every visit genuinely originates from your chosen location.' },
        { question: 'Does buying targeted traffic that converts actually work?', answer: 'The traffic itself generates realistic engagement signals (dwell time, scrolling, page views) that improve your site\'s SEO metrics. For e-commerce conversions, it helps by improving your search rankings which leads to more genuine converting visitors.' },
        { question: 'How is keyword targeted traffic different?', answer: 'Keyword targeted traffic arrives at your site through search-like pathways, mimicking visitors who found you through specific search queries. This creates organic search signals that can improve your rankings for those exact keywords.' },
        { question: 'Will targeted traffic appear in Google Analytics?', answer: 'Yes, 100%. All targeted traffic registers in GA4 with accurate geographic data, device information, session duration, and referrer paths — exactly matching the targeting parameters you set.' }
    ],

    relatedLinks: [
        { label: 'Geo-Targeted Traffic', to: '/he/geo-targeted-traffic' },
        { label: 'Buy Website Traffic', to: '/buy-website-traffic' },
        { label: 'Local SEO Traffic Bot', to: '/he/local-seo-traffic-bot' }
    ],

    productName: 'Buy Targeted Traffic - Precision Audience Matching',
    productDescription: 'Buy targeted traffic from specific countries, devices, and niches. Real residential visitors precisely matched to your audience.',
    translations: {
        'navFeatures': 'תכונות',
        'navPricing': 'מחירים',
        'navFaq': 'שאלות נפוצות',
        'navLogin': 'כניסה ללוח בקרה',
        'heroBadge': 'שירות תנועה פרמיום',
        'heroCtaPrimary': 'נסו בחינם',
        'heroCtaSecondary': 'צפו במחירים',
        'heroTrust': 'מהימן על ידי 10,000+ משתמשים',
        'heroMicrocopy': 'לא נדרש כרטיס אשראי. 100% בטוח ומאומת באנליטיקס.',
        'heroLiveLabel': 'חי',
        'heroTrafficIncrease': 'עלייה בתנועה',
        'heroActiveLabel': 'פעיל',
        'heroVisitorsServed': 'מבקרים שהוגשו',
        'socialProofLabel': 'מהימן על ידי:',
        'problemWithout': 'בלי',
        'problemWith': 'עם',
        'problemBullets': [
                'תנועה אורגנית נמוכה מגבילה את הצמיחה שלכם',
                'דירוגים נמוכים בחיפוש פוגעים בהמרות',
                'מתחרים שולטים במילות המפתח שלכם',
                'שיעורי נטישה גבוהים פוגעים ב-SEO',
        ],
        'statsCampaigns': 'קמפיינים פעילים יומיים',
        'statsCountries': 'מדינות',
        'statsUptime': 'זמן פעילות',
        'statsSupport': 'תמיכה',
        'featuresSubtitle': 'כל מה שאתם צריכים לשיפור ביצועי האתר שלכם.',
        'howItWorksH2': '3 צעדים פשוטים',
        'howItWorksSteps': [
                {
                        'title': 'הגדרת הקמפיין',
                        'desc': 'בחרו מדינה, מכשיר והגדרות התנהגות תוך דקות.'
                },
                {
                        'title': 'הפעלת הרשת',
                        'desc': 'הפלטפורמה שלנו מפנה מבקרים אמיתיים לכתובת ה-URL שלכם.'
                },
                {
                        'title': 'ניתוח התוצאות',
                        'desc': 'צפו בשיפור המדדים שלכם ב-Google Analytics 4.'
                },
        ],
        'pricingH2': 'שקוף',
        'pricingSubtitle': 'רכישה חד פעמית. ללא מנויים. קרדיטים לא פגים לעולם.',
        'pricingMostPopular': 'הכי פופולרי',
        'pricingVisitors': 'מבקרים',
        'pricingGetStarted': 'התחילו עכשיו',
        'pricingGuarantee': 'החזר כספי תוך 30 יום',
        'pricingDelivery': 'משלוח מיידי (~5 דק׳)',
        'pricingCountries': '190+ מדינות',
        'faqH2': 'שאלות נפוצות',
        'ctaSubtitle': 'הצטרפו ל-10,000+ לקוחות ותגבירו את תנועת האתר שלכם היום.',
        'ctaButtonPrimary': 'התחילו בחינם',
        'ctaButtonSecondary': 'צפו במחירים',
        'ctaMicrocopy': 'לא נדרש כרטיס אשראי. 100% בטוח ומאומת באנליטיקס.',
        'footerDescription': 'פתרונות למקצועני SEO וסוכנויות דיגיטליות ברחבי העולם.',
        'footerRelatedServices': 'שירותים קשורים',
        'footerCompany': 'חברה',
        'footerAbout': 'אודותינו',
        'footerBlog': 'בלוג',
        'footerHelpCenter': 'מרכז עזרה',
        'footerPrivacy': 'מדיניות פרטיות',
        'footerImpressum': 'מידע משפטי',
        'footerStatus': 'כל המערכות פועלות',
        'footerBuyTraffic': 'קנו תנועת אתר',
    },
};

const HeBuyTargetedTraffic: React.FC = () => {
    return <TrafficLandingTemplate config={config} />;
};

export default HeBuyTargetedTraffic;
